import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Grid2, Typography, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Logo from '../../assets/images/logo_footer.svg';
import { brandTokens } from '../single-page/theme';
import { useModals } from '../hooks/useModals';
import { ROUTES } from '../single-page/constants/routes';

/**
 * Official marketing contacts. Leave empty to omit contact rows (no placeholders).
 * Fill when product provides real channels.
 */
export const OFFICIAL_CONTACTS = {
  email: null,
  phone: null,
  whatsapp: null,
};

const Root = styled('footer')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  margin: 0,
  backgroundColor: brandTokens.surfaceLowest,
  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  color: brandTokens.onSurfaceVariant,
}));

const Inner = styled(Box)(({ theme }) => ({
  maxWidth: 1280,
  margin: '0 auto',
  padding: theme.spacing(6, 3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 2),
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    height: '32px',
  },
}));

const ColumnTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'GT Haptik, Roboto, sans-serif',
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: brandTokens.cyan,
  marginBottom: theme.spacing(2),
}));

const FooterLink = styled(MuiLink)(({ theme }) => ({
  display: 'block',
  fontFamily: 'Roboto, sans-serif',
  fontSize: 14,
  color: brandTokens.onSurfaceVariant,
  textDecoration: 'none',
  marginBottom: theme.spacing(1.5),
  cursor: 'pointer',
  '&:hover': {
    color: brandTokens.green,
  },
}));

const BrandText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Roboto, sans-serif',
  fontSize: 14,
  lineHeight: 1.6,
  color: brandTokens.onSurfaceVariant,
  maxWidth: 280,
}));

const BottomBar = styled(Box)(({ theme }) => ({
  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(3),
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const scrollToId = (id) => {
  if (window.location.pathname !== '/') {
    window.location.assign(`/#${id}`);
    return;
  }
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Footer = () => {
  const { setContactOpen } = useModals();
  const year = new Date().getFullYear();
  const hasContacts =
    Boolean(OFFICIAL_CONTACTS.email) ||
    Boolean(OFFICIAL_CONTACTS.phone) ||
    Boolean(OFFICIAL_CONTACTS.whatsapp);

  return (
    <Root>
      <Inner>
        <Grid2 container spacing={4}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <RouterLink to={ROUTES.HOME}>
              <LogoImage src={Logo} alt="AutoValid" />
            </RouterLink>
            <BrandText>
              Tecnologia e inteligência de dados para trazer segurança e
              transparência ao mercado automotivo brasileiro.
            </BrandText>
          </Grid2>

          <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
            <ColumnTitle>Institucional</ColumnTitle>
            <FooterLink component="button" type="button" onClick={() => scrollToId('aboutUs')}>
              Sobre Nós
            </FooterLink>
            <FooterLink component="button" type="button" onClick={() => setContactOpen(true)}>
              Fale Conosco
            </FooterLink>
          </Grid2>

          <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
            <ColumnTitle>Consultas</ColumnTitle>
            <FooterLink component="button" type="button" onClick={() => scrollToId('plateSearch')}>
              Consultar Placa
            </FooterLink>
            <FooterLink component="button" type="button" onClick={() => scrollToId('howItWorks')}>
              Como Funciona
            </FooterLink>
            <FooterLink component="button" type="button" onClick={() => scrollToId('meetOurInspections')}>
              Planos
            </FooterLink>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4, md: 3 }}>
            <ColumnTitle>Suporte</ColumnTitle>
            {hasContacts ? (
              <>
                {OFFICIAL_CONTACTS.email && (
                  <FooterLink href={`mailto:${OFFICIAL_CONTACTS.email}`}>
                    {OFFICIAL_CONTACTS.email}
                  </FooterLink>
                )}
                {OFFICIAL_CONTACTS.phone && (
                  <FooterLink href={`tel:${OFFICIAL_CONTACTS.phone}`}>
                    {OFFICIAL_CONTACTS.phone}
                  </FooterLink>
                )}
                {OFFICIAL_CONTACTS.whatsapp && (
                  <FooterLink
                    href={`https://wa.me/${OFFICIAL_CONTACTS.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp
                  </FooterLink>
                )}
              </>
            ) : (
              <FooterLink component="button" type="button" onClick={() => setContactOpen(true)}>
                Abrir formulário de contato
              </FooterLink>
            )}
          </Grid2>
        </Grid2>

        <BottomBar>
          <Typography
            sx={{
              fontSize: 12,
              color: brandTokens.onSurfaceVariant,
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            © {year} Autovalid. Todos os direitos reservados.
          </Typography>
        </BottomBar>
      </Inner>
    </Root>
  );
};

export default Footer;
